import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsrProxyAdminService, PromptsService } from 'app/API';
import { adminOrganizationsKeys } from 'app/queries/adminOrganizations/adminOrganizationsKeys';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { createFetchOrganizationModelChoicesQueryFn } from './api/createFetchOrganizationModelChoicesQueryFn';
import { getModelChoiceErrorMessage } from './helpers/getModelChoiceErrorMessage';
import { getReasoningEffortForModelChange } from './helpers/getReasoningEffortForModelChange';
import { getSupportedReasoningEfforts } from './helpers/getSupportedReasoningEfforts';
import { REASONING_EFFORTS, isReasoningEffort } from './helpers/isReasoningEffort';
import { Content } from './styles/Content';
import { TOrganizationModelChoices } from './types/TOrganizationModelChoices';
import { TPromptSelection } from './types/TPromptSelection';
import { TProps } from './types/TProps';
import { TReasoningEffort } from './types/TReasoningEffort';

export const AdminAIModelChoiceModal = ({ organization }: TProps) => {
  const { closeModal } = useModalContext();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const modelChoicesKey = adminOrganizationsKeys.modelChoices(organization.id);

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [bulkModel, setBulkModel] = useState('');
  const [bulkEffort, setBulkEffort] = useState('');
  const [selectedAsrModelDraft, setSelectedAsrModelDraft] = useState<string | null>(null);

  const modelChoicesQuery = useQuery({
    queryKey: modelChoicesKey,
    queryFn: createFetchOrganizationModelChoicesQueryFn(call),
    gcTime: 0,
    staleTime: Infinity,
  });

  const failedSections = modelChoicesQuery.data?.failedSections;

  useEffect(() => {
    if (failedSections?.length) {
      toast.error(`Failed to load ${failedSections.join(', ')}. Reopen the modal to retry.`, {
        id: 'model-choice-load',
      });
    }
  }, [failedSections]);

  const versions = modelChoicesQuery.data?.versions ?? [];
  const allPrompts = modelChoicesQuery.data?.prompts ?? [];
  const promptModels = modelChoicesQuery.data?.promptModels ?? {};
  const promptReasoningEfforts = modelChoicesQuery.data?.promptReasoningEfforts ?? {};
  const asrDefaultModel = modelChoicesQuery.data?.asrDefaultModel ?? '';
  const asrModels = modelChoicesQuery.data?.asrModels ?? [];
  const asrModel = modelChoicesQuery.data?.asrModel ?? '';
  const selectedAsrModel = selectedAsrModelDraft ?? asrModel;

  const updateModelChoices = async (update: (current: TOrganizationModelChoices) => TOrganizationModelChoices) => {
    await queryClient.cancelQueries({ queryKey: modelChoicesKey });
    queryClient.setQueryData<TOrganizationModelChoices>(modelChoicesKey, (current) =>
      current ? update(current) : current,
    );
  };

  const setPromptSelections = (selections: TPromptSelection[]) =>
    updateModelChoices((current) => ({
      ...current,
      promptModels: {
        ...current.promptModels,
        ...Object.fromEntries(selections.map(({ slug, model }) => [slug, model])),
      },
      promptReasoningEfforts: {
        ...current.promptReasoningEfforts,
        ...Object.fromEntries(
          selections.flatMap(({ slug, reasoningEffort }) =>
            reasoningEffort === undefined ? [] : [[slug, reasoningEffort]],
          ),
        ),
      },
    }));

  const updatePromptSelection = ({ slug, model, reasoningEffort }: TPromptSelection) =>
    call(
      () =>
        PromptsService.updatePromptModelForOrganization({
          oid: organization.id,
          slug,
          requestBody: reasoningEffort === undefined ? { model } : { model, reasoningEffort },
        }),
      { dontRedirectOnError: true },
    );

  const updatePromptSelections = async (selections: TPromptSelection[]) => {
    const successfulSelections: TPromptSelection[] = [];

    for (const selection of selections) {
      try {
        await updatePromptSelection(selection);
        successfulSelections.push(selection);
      } catch (error) {
        toast.error(getModelChoiceErrorMessage(error));
      }
    }

    return successfulSelections;
  };

  const promptModelMutation = useMutation({
    mutationFn: updatePromptSelection,
    onMutate: () => {
      toast.loading('Checking if model is applicable for this feature...', { id: 'model-check' });
    },
    onSuccess: async (_, selection) => {
      await setPromptSelections([selection]);
      toast.success(
        selection.reasoningEffort === null
          ? 'Model updated. The new model does not support the reasoning effort, so it was reset to default.'
          : 'Model updated successfully',
      );
    },
    onError: (error) => {
      toast.error(getModelChoiceErrorMessage(error));
    },
    onSettled: () => {
      toast.remove('model-check');
    },
  });

  const promptEffortMutation = useMutation({
    mutationFn: updatePromptSelection,
    onMutate: () => {
      toast.loading('Updating reasoning effort...', { id: 'effort-update' });
    },
    onSuccess: async (_, selection) => {
      await setPromptSelections([selection]);
      toast.success('Reasoning effort updated successfully');
    },
    onError: (error) => {
      toast.error(getModelChoiceErrorMessage(error));
    },
    onSettled: () => {
      toast.remove('effort-update');
    },
  });

  const bulkPromptModelMutation = useMutation({
    mutationFn: (model: string) =>
      updatePromptSelections(
        allPrompts.map((prompt) => ({
          slug: prompt.slug,
          model,
          reasoningEffort: getReasoningEffortForModelChange(model, promptReasoningEfforts[prompt.slug]),
        })),
      ),
    onMutate: (model) => {
      toast.loading(model ? 'Updating all model selections...' : 'Clearing all model selections...', {
        id: 'model-bulk',
      });
    },
    onSuccess: async (successfulSelections, model) => {
      await setPromptSelections(successfulSelections);

      if (successfulSelections.length === allPrompts.length) {
        toast.success(model ? 'All models were updated' : 'All models were unset');
      } else {
        toast.error(`${allPrompts.length - successfulSelections.length} model settings failed to update`);
      }
    },
    onSettled: () => {
      toast.remove('model-bulk');
    },
  });

  const bulkPromptEffortMutation = useMutation({
    mutationFn: async (reasoningEffort: TReasoningEffort | null) => {
      const selections = allPrompts
        .map((prompt) => ({ slug: prompt.slug, model: promptModels[prompt.slug] ?? '', reasoningEffort }))
        .filter(
          ({ model }) => reasoningEffort === null || getSupportedReasoningEfforts(model).includes(reasoningEffort),
        );
      const successfulSelections = await updatePromptSelections(selections);

      return { successfulSelections, attempted: selections.length };
    },
    onMutate: (reasoningEffort) => {
      toast.loading(reasoningEffort ? 'Updating all reasoning efforts...' : 'Clearing all reasoning efforts...', {
        id: 'effort-bulk',
      });
    },
    onSuccess: async ({ successfulSelections, attempted }, reasoningEffort) => {
      await setPromptSelections(successfulSelections);

      const failed = attempted - successfulSelections.length;
      const skipped = allPrompts.length - attempted;

      if (failed) {
        toast.error(`${failed} reasoning effort settings failed to update`);
      } else if (skipped) {
        toast.success(
          `Reasoning effort updated. ${skipped} ${skipped === 1 ? 'feature was' : 'features were'} skipped because the model does not support "${reasoningEffort}".`,
        );
      } else {
        toast.success(
          reasoningEffort ? 'All reasoning efforts were updated' : 'All reasoning efforts were reset to default',
        );
      }
    },
    onSettled: () => {
      toast.remove('effort-bulk');
    },
  });

  const asrModelMutation = useMutation({
    mutationFn: async (model: string) => {
      if (model) {
        await call(
          () =>
            AsrProxyAdminService.setOrganizationModel({
              organizationId: organization.id,
              requestBody: { model },
            }),
          { dontRedirectOnError: true },
        );
        return;
      }

      await call(() => AsrProxyAdminService.deleteOrganizationConfig({ organizationId: organization.id }), {
        dontRedirectOnError: true,
      });
    },
    onMutate: (model) => {
      toast.loading(model ? 'Updating ASR model...' : 'Restoring default ASR model...', { id: 'asr-model' });
    },
    onSuccess: async (_, model) => {
      await updateModelChoices((current) => ({ ...current, asrModel: model }));
      toast.success(model ? 'ASR model updated successfully' : 'Default ASR model restored');
    },
    onError: (error) => {
      toast.error(getModelChoiceErrorMessage(error));
    },
    onSettled: () => {
      toast.remove('asr-model');
    },
  });

  const handleBulkUpdate = (model: string) => {
    if (!allPrompts.length) {
      return;
    }

    bulkPromptModelMutation.mutate(model);
  };

  const handleBulkEffortUpdate = (reasoningEffort: TReasoningEffort | null) => {
    if (!allPrompts.length) {
      return;
    }

    bulkPromptEffortMutation.mutate(reasoningEffort);
  };

  const handlePromptModelChange = (slug: string, model: string) =>
    promptModelMutation.mutate({
      slug,
      model,
      reasoningEffort: getReasoningEffortForModelChange(model, promptReasoningEfforts[slug]),
    });

  const handlePromptEffortChange = (slug: string, value: string) => {
    const reasoningEffort = isReasoningEffort(value) ? value : null;

    if (value && !reasoningEffort) {
      return;
    }

    promptEffortMutation.mutate({ slug, model: promptModels[slug] ?? '', reasoningEffort });
  };

  const getPromptEffortOptions = (slug: string) => {
    const supportedEfforts = getSupportedReasoningEfforts(promptModels[slug] ?? '');
    const storedEffort = promptReasoningEfforts[slug];
    const efforts =
      storedEffort && !supportedEfforts.includes(storedEffort) ? [storedEffort, ...supportedEfforts] : supportedEfforts;

    return [{ label: 'default', value: '' }, ...efforts.map((value) => ({ label: value, value }))];
  };

  const isLoading =
    modelChoicesQuery.isLoading ||
    promptModelMutation.isPending ||
    bulkPromptModelMutation.isPending ||
    promptEffortMutation.isPending ||
    bulkPromptEffortMutation.isPending ||
    asrModelMutation.isPending;

  const asrModelOptions =
    selectedAsrModel && !asrModels.includes(selectedAsrModel) ? [selectedAsrModel, ...asrModels] : asrModels;

  return (
    <ModalWrapper width="800px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Set ASR and LLM models for organization</ModalTitle>
      <Content>
        <section className="mb-6 border-b border-[var(--color-border)] pb-6">
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">ASR model</h3>
          <div className="grid grid-cols-[minmax(0,1fr)_120px] items-end gap-3">
            <Select
              value={selectedAsrModel}
              options={[
                {
                  label: asrDefaultModel ? 'default (' + asrDefaultModel + ')' : 'default',
                  value: '',
                },
                ...asrModelOptions.map((value) => ({ label: value, value })),
              ]}
              onChange={(_, value) => setSelectedAsrModelDraft(value)}
              label="ASR model"
              required
              name="asr-model"
              disabled={isLoading}
            />
            <OutlinedButton
              className="btn--md w-full"
              onClick={() => asrModelMutation.mutate(selectedAsrModel)}
              disabled={isLoading || selectedAsrModel === asrModel}
            >
              Set
            </OutlinedButton>
          </div>
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold text-[var(--color-text)]">LLM prompt models</h3>
          <div className="mb-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_248px] items-end gap-3">
            <Select
              value={bulkModel}
              options={versions.map((value) => ({ label: value, value }))}
              onChange={(_, value) => setBulkModel(value)}
              label="Model for all features"
              required
              name="bulk-model"
              disabled={isLoading || !versions.length}
            />
            <div className="flex gap-2">
              <OutlinedButton
                className="btn--md flex-1"
                onClick={() => handleBulkUpdate(bulkModel)}
                disabled={isLoading || !bulkModel}
              >
                Set all
              </OutlinedButton>
              <OutlinedButton className="btn--md flex-1" onClick={() => handleBulkUpdate('')} disabled={isLoading}>
                Unset all
              </OutlinedButton>
            </div>
          </div>
          <div className="mb-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_248px] items-end gap-3">
            <Select
              value={bulkEffort}
              options={REASONING_EFFORTS.map((value) => ({ label: value, value }))}
              onChange={(_, value) => setBulkEffort(value)}
              label="Reasoning effort for all features"
              required
              name="bulk-effort"
              disabled={isLoading}
            />
            <div className="flex gap-2">
              <OutlinedButton
                className="btn--md flex-1"
                onClick={() => isReasoningEffort(bulkEffort) && handleBulkEffortUpdate(bulkEffort)}
                disabled={isLoading || !isReasoningEffort(bulkEffort)}
              >
                Set all
              </OutlinedButton>
              <OutlinedButton
                className="btn--md flex-1"
                onClick={() => handleBulkEffortUpdate(null)}
                disabled={isLoading}
              >
                Unset all
              </OutlinedButton>
            </div>
          </div>

          <DisclosureSection
            title="Advanced settings"
            open={advancedOpen}
            setOpenState={setAdvancedOpen}
            loading={isLoading}
            className="advancedSection"
            titleClassName="!bg-[var(--color-surface)]"
          >
            <div className="flex flex-col w-full">
              {allPrompts.map((prompt) => {
                const effortOptions = getPromptEffortOptions(prompt.slug);

                return (
                  <div
                    key={prompt.slug}
                    className="mb-2.5 grid grid-cols-[minmax(0,1fr)_260px_160px] items-center gap-3 text-sm"
                  >
                    <div className="break-words">{prompt.slug}</div>
                    <Select
                      value={promptModels[prompt.slug] ?? ''}
                      options={[{ label: 'default', value: '' }, ...versions.map((value) => ({ label: value, value }))]}
                      onChange={(_, value) => handlePromptModelChange(prompt.slug, value)}
                      label="Model"
                      required
                      name="model"
                    />
                    <Select
                      value={promptReasoningEfforts[prompt.slug] ?? ''}
                      options={effortOptions}
                      onChange={(_, value) => handlePromptEffortChange(prompt.slug, value)}
                      label="Reasoning effort"
                      required
                      name="reasoning-effort"
                      disabled={effortOptions.length === 1}
                    />
                  </div>
                );
              })}
            </div>
          </DisclosureSection>
        </section>

        <div className="buttonGroup">
          <OutlinedButton onClick={closeModal}>Close</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
};
